/****
 
 * NOTES REGARDING THE CACHED DISCOUNT ISSUE IN SHOPIFY:
    When navigating from checkout to cart with a discount code in checkout,
    Shopify will alter their endpoint data to discount the price we originally use. This results in price variations.
    As a fix, we must add the line_level_total_discount property and divide it by the item quantity to get consistent pricing through the user journey.
    
    example 1, (user has not visited checkout): 2x of a $50 item, 10% discount. (50 + 0/2)(.90) *2 = $90.
    example 2, (user has visited checkout): 2x of a $50 item, 10% discount. (45 + 10/2)(.90) *2 = $90. 

    
 */

//given a subtotal of eligible tiered discounting items, get the current tier discount and return the discounted price
export function calculateTieredDiscount(currentSubtotal, price) {
  const tiers = window.Brooklinen.promo?.discounts?.tiers;
  const tier1Threshold = tiers.tier1.threshold,
    tier2Threshold = tiers.tier2.threshold,
    tier3Threshold = tiers.tier3.threshold,
    tier1Eligible = currentSubtotal >= tier1Threshold && currentSubtotal < tier2Threshold,
    tier2Eligible = currentSubtotal >= tier2Threshold && currentSubtotal < tier3Threshold,
    tier3Eligible = currentSubtotal >= tier3Threshold;

  let discountPercent = 0;
  if (tier1Eligible) {
    discountPercent = parseFloat(tiers.tier1.amount) / 100;
  } else if (tier2Eligible) {
    discountPercent = parseFloat(tiers.tier2.amount) / 100;
  } else if (tier3Eligible) {
    discountPercent = parseFloat(tiers.tier3.amount) / 100;
  }
  const discountAmount = 1 - discountPercent;
  const discountedPrice = Math.round((price * discountAmount).toFixed(2));
  return discountedPrice;
}

export function calculateSWD(price, promoQualified, cart) {
  if (!window.Brooklinen?.promo) return false;
  const swdEnabled = window.Brooklinen?.promo?.discounts?.swd?.enabled;
  const promo = promoQualified?.value ?? promoQualified;

  if (promo?.swd && swdEnabled) {
    const discountPercent = window?.Brooklinen?.promo?.discounts?.swd.amount / 100;
    const discountAmount = 1 - discountPercent;
    const discountedPrice = price * discountAmount;
    return discountedPrice;
  } else if (promo?.tiered) {
    return calculateTieredDiscount(getTieredSubtotal(cart), price);
  } else {
    return price;
  }
}

export function getSubtotal(cart, isOriginalSubtotal, promoQualified) {
  let sum = 0,
    discounts = window.Brooklinen.promo.discounts,
    swdTags = discounts?.swd?.tags?.split(','),
    tierTags = discounts?.tiers?.tags?.split(',');

  for (let i = 0; i < cart.length; i++) {
    const cartItem = cart[i];
    const promoEligible = cartItem.tags.some((tag) => swdTags?.includes(tag) || tierTags?.includes(tag));
    const originalCartItemPrice = cartItem.price + cartItem.line_level_total_discount / cartItem.quantity;
    //determine whether to show the default subtotal without price reductions, or the discounted subtotal
    if (isOriginalSubtotal) {
      // if the cart item is a bundle, use the sum of its components to calculate the original subtotal
      if (cartItem.components.length > 0) {
        for (let j = 0; j < cartItem.components.length; j++) {
          const componentPrice = cartItem.components[j]?.compareAtPrice || cartItem.components[j]?.component_price;
          sum += componentPrice * cartItem.quantity;
        }
      } //if the from price is greater than the discounted price plus the discount amount, use the from price when calculating original subtotal
      else if ((parseInt(cartItem?.fromPrice?.value) > originalCartItemPrice && cartItem?.price > 0) || cartItem?.bmsm_enabled) {
        sum += parseInt(cartItem?.fromPrice?.value) * cartItem.quantity;
      } else {
        sum += originalCartItemPrice * cartItem.quantity;
      }
    } else {
      const bmsmDiscount = cartItem.bmsm_enabled ? calcBMSMSavings(cartItem) : 1;

      let cachedPrice = 0;

      const originalPrice = cartItem?.price + cartItem?.line_level_total_discount / cartItem?.quantity;
      if (promoEligible && !cartItem?.bmsm_enabled) {
        //calculate actual subtotal by adding the discounted price of a line item

        cachedPrice = calculateSWD(originalPrice, promoQualified, cart) * cartItem?.quantity;

        //shopify rounds its line_level_total_discount, which can throw off subtotal calculations by a cent or two.
        //This is a workaround to ensure that the subtotal remains in parity with checkout

        cachedPrice = Math.ceil(cachedPrice) == cartItem?.price || Math.round(cachedPrice) == cartItem?.price ? cartItem?.price : cachedPrice;
      } else if (cartItem?.bmsm_enabled) {
        //when calculating subtotal with a BMSM product, use the from price as a starting point instead of the price value, and perform the BMSM discount manually
        cachedPrice = calculateSWD(parseInt(cartItem.fromPrice.value) * bmsmDiscount, promoQualified, cart) * cartItem?.quantity;
      } else {
        //if there is no SWD or tiered discounting to calculate, use the price * quantity
        cachedPrice = originalPrice * cartItem?.quantity;

        cachedPrice = Math.ceil(cachedPrice) == cartItem?.price || Math.round(cachedPrice) == cartItem?.price ? cartItem?.price : cachedPrice;
      }
      sum += cachedPrice;
    }
  }
  return sum;
}

export function getTieredSubtotal(cart) {
  const tags = window.Brooklinen?.promo?.discounts?.tiers?.tags?.split(',');
  if (cart) {
    const eligibleItems = cart.filter((item) => {
      return item.tags.filter((cartItemTag) => tags.includes(cartItemTag));
    });

    let sum = 0;
    eligibleItems.reduce((subtotal, item) => {
      if (!item?.discount_eligible) return false;
      const cachedPrice = item.bmsm_enabled ? item.fromPrice?.value * calcBMSMSavings(item) : item.price + item.line_level_total_discount / item.quantity;
      sum += cachedPrice * item.quantity;
      subtotal += sum;
      return parseFloat((subtotal / 100).toFixed(2));
    }, 0);
    return parseFloat((sum / 100).toFixed(2));
  }
}

export function formattedPrice(price) {
  if (typeof price === 'boolean' || !price) {
    console.log('error: boolean or undefined value entered for price');
    return 0;
  }
  let number;

  if (typeof price === 'string') {
    number = parseFloat(price);
  } else if (typeof price === 'number') {
    number = price;
  }
  const format = number.toLocaleString(`${window.Shopify.locale}-${window.Shopify.country}`, {
    style: 'currency',
    currency: `${window.Shopify.currency.active}`,
  });
  return formatTrailingZeros(format);
}

export function calcBMSMSavings(item) {
  const tiers = item.tags
    .find((tag) => tag.includes('BMSM Tiers:'))
    .split(':')[1]
    .split('/');
  const quantity = item.quantity;
  let percentOff = 1;
  if (tiers) {
    tiers.forEach((tier, index) => {
      const quantityThreshold = parseInt(tier.trim().split('-')[0]);
      let discountAmount = parseInt(tier.trim().split('-')[1]),
        nextQuantityThreshold;

      if (tiers.length != index + 1) nextQuantityThreshold = parseInt(tiers[index + 1].trim().split('-')[0]);
      if ((quantity >= quantityThreshold && quantity < nextQuantityThreshold) || (quantity >= quantityThreshold && !nextQuantityThreshold)) percentOff = 1 - discountAmount / 100;
    });
    return percentOff;
  }
}

export function formatMoneyCents(amount) {
  return `${(amount / 100).toFixed(2)}`;
}

export const formatTrailingZeros = (amount) => {
  if (amount.includes('.00')) {
    const withoutZeros = amount.replace('.00', '').trim();
    return withoutZeros;
  }
  return amount;
};
